import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTaskView } from '../../context/TaskViewContext';
import { createPortal } from 'react-dom';
import {
  type CollisionDetection,
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  rectIntersection,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Target,
  Eye,
  Check,
  CheckCheck,
  Columns3,
  Copy,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Folder,
  GripVertical,
  Info,
  List,
  MoreHorizontal,
  Plus,
  Rows3,
  Trash2,
} from 'lucide-react';
import { brandColors } from '@/design';
import type { TaskPlan, ActionItem, ActionItemStatus, TodoProject, TodoProjectColorKey } from '../../types';
import { ProjectPickerPopover } from '../todos/ProjectAssignButton';
import {
  type ActionItemParentKind,
  makeActionItemDragId,
} from '../../utils/todoDragIds';

export type TaskCardViewMode = 'list' | 'kanban' | 'bullets';
export type KanbanOrientation = 'vertical' | 'horizontal';
const KANBAN_COLUMNS: Array<{ id: ActionItemStatus; label: string }> = [
  { id: 'todo', label: 'To do' },
  { id: 'in_progress', label: 'Doing' },
  { id: 'done', label: 'Done' },
];

function getActionItemStatus(item: ActionItem): ActionItemStatus {
  if (item.status) return item.status;
  return item.completed ? 'done' : 'todo';
}

function isActionItemDone(item: ActionItem): boolean {
  return getActionItemStatus(item) === 'done';
}

function getRadioVisualStyle(status: ActionItemStatus, done: boolean): {
  border: string;
  background: string;
  boxShadow?: string;
} {
  if (done) {
    return {
      border: 'none',
      background: 'var(--color-success)',
    };
  }
  if (status === 'in_progress') {
    return {
      border: `2px solid ${brandColors.lightBlue}`,
      background: 'transparent',
      boxShadow: `0 0 0 2px ${brandColors.lightBlue}22`,
    };
  }
  return {
    border: '2px solid var(--color-text-secondary)',
    background: 'transparent',
  };
}

function taskToMarkdown(task: TaskPlan): string {
  const lines: string[] = [`## ${task.title}`];
  if (task.summary) lines.push('', task.summary);
  lines.push('');
  task.actionItems
    .sort((a, b) => a.order - b.order)
    .forEach((item) => {
      lines.push(`- [${isActionItemDone(item) ? 'x' : ' '}] ${item.text}`);
    });
  return lines.join('\n');
}

// ─── Context Menu ─────────────────────────────────────────────────────────────

function ContextMenu({
  x,
  y,
  onDelete,
  onClose,
}: {
  x: number;
  y: number;
  onDelete: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="fixed z-50 py-1 rounded-lg shadow-lg"
      style={{
        left: x,
        top: y,
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-border)',
        minWidth: 140,
      }}
    >
      <button
        onClick={() => {
          onDelete();
          onClose();
        }}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[var(--color-surface)] transition-colors"
        style={{ color: 'var(--color-error)' }}
      >
        <Trash2 size={14} />
        Remove
      </button>
    </div>
  );
}

// ─── Sortable Action Item ────────────────────────────────────────────────────

function SortableActionItem({
  item,
  taskId,
  onToggle,
  onDelete,
  layout = 'list',
  animateToDone = false,
  animateToTodo = false,
  showDropIndicatorTop = false,
  sortableId,
  /** Optimistic override: treat the item as done visually before Firestore confirms. */
  waveDone = false,
}: {
  item: ActionItem;
  taskId: string;
  onToggle: (taskId: string, actionItemId: string, completed: boolean) => Promise<void>;
  onDelete: (taskId: string, actionItemId: string) => Promise<void>;
  layout?: 'list' | 'kanban';
  animateToDone?: boolean;
  animateToTodo?: boolean;
  showDropIndicatorTop?: boolean;
  sortableId?: string;
  waveDone?: boolean;
}) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const done = isActionItemDone(item);
  const effectiveDone = done || waveDone;
  const status = effectiveDone ? 'done' : getActionItemStatus(item);
  const radioVisualStyle = getRadioVisualStyle(status, effectiveDone);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId ?? item.id });
  const itemRef = useRef<HTMLDivElement | null>(null);
  const setItemNodeRef = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node);
    itemRef.current = node;
  }, [setNodeRef]);

  useEffect(() => {
    if (!itemRef.current) return;
    if (!animateToDone && !animateToTodo) return;
    itemRef.current.animate(
      animateToDone
        ? [
            { opacity: 0.35, transform: 'translateX(-18px) scale(0.98)' },
            { opacity: 1, transform: 'translateX(0) scale(1)' },
          ]
        : [
            { opacity: 0.35, transform: 'translateX(18px) scale(0.98)' },
            { opacity: 1, transform: 'translateX(0) scale(1)' },
          ],
      { duration: 1120, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
  }, [animateToDone, animateToTodo]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.25 : 1,
    zIndex: isDragging ? 1 : undefined,
    paddingTop: showDropIndicatorTop && !isDragging ? 12 : 0,
    border: isDragging ? `1px solid ${brandColors.teal}99` : undefined,
    background: isDragging ? `${brandColors.teal}18` : undefined,
    boxShadow: isDragging ? 'var(--shadow-lg)' : undefined,
    ...(layout === 'kanban'
      ? {
          width: '100%',
          background: isDragging ? `${brandColors.teal}18` : 'var(--color-surface-elevated)',
          border: isDragging
            ? `1px solid ${brandColors.teal}99`
            : '1px solid var(--color-border)',
        }
      : {}),
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <div
        ref={setItemNodeRef}
        style={style}
        className={`relative flex items-start gap-3 px-2 py-2 rounded-lg transition-[background-color,padding] group cursor-grab active:cursor-grabbing touch-none ${
          layout === 'kanban'
            ? ''
            : 'hover:bg-[var(--color-surface)]'
        }`}
        data-task-view-item="true"
        onContextMenu={handleContextMenu}
        aria-grabbed={isDragging}
        {...attributes}
        {...listeners}
      >
        {showDropIndicatorTop && !isDragging && (
          <div className="absolute left-2 right-2 top-1.5 pointer-events-none">
            <div
              className="h-[3px] rounded-full"
              style={{
                background: 'var(--color-success)',
                boxShadow: '0 0 0 2px color-mix(in srgb, var(--color-success) 28%, transparent)',
              }}
            />
          </div>
        )}
        <div className="flex items-start gap-3 text-left flex-1 min-w-0">
          <div
            className="shrink-0 mt-0.5"
          >
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onToggle(taskId, item.id, !effectiveDone)}
              className="w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-200"
              style={radioVisualStyle}
              aria-label={effectiveDone ? 'Mark task as not done' : 'Mark task as done'}
            >
              {effectiveDone ? <Check size={12} color="white" strokeWidth={3} /> : null}
            </button>
          </div>
          <div className="min-w-0">
            <span
              className={`text-sm leading-snug transition-colors duration-200 ${
                effectiveDone
                  ? 'text-[var(--color-text-muted)] line-through'
                  : 'text-[var(--color-text-primary)]'
              }`}
            >
              {item.text}
            </span>
            {status === 'in_progress' && (
              <div>
                <span
                  className="text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: brandColors.lightBlue }}
                >
                  Doing
                </span>
              </div>
            )}
          </div>
        </div>
        <span
          className="shrink-0 mt-0.5 p-0.5 rounded text-[var(--color-text-muted)] opacity-40 group-hover:opacity-70 transition-opacity"
          aria-hidden="true"
        >
          <GripVertical size={16} />
        </span>
      </div>
      {menu && createPortal(
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onDelete={() => onDelete(taskId, item.id)}
          onClose={() => setMenu(null)}
        />,
        document.body
      )}
    </>
  );
}

function DragActionItemOverlay({ item }: { item: ActionItem }) {
  const done = isActionItemDone(item);
  const status = getActionItemStatus(item);
  const radioVisualStyle = getRadioVisualStyle(status, done);
  return (
    <div
      className="flex items-start gap-3 px-2 py-2 rounded-lg group min-w-[220px] max-w-[420px]"
      style={{
        border: `1px solid ${brandColors.teal}AA`,
        background: `${brandColors.teal}1F`,
        boxShadow: 'var(--shadow-xl)',
      }}
    >
      <div
        className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors duration-200"
        style={radioVisualStyle}
      >
        {done ? <Check size={12} color="white" strokeWidth={3} /> : null}
      </div>
      <div className="min-w-0 flex-1">
        <span
          className={`text-sm leading-snug transition-colors duration-200 ${
            done
              ? 'text-[var(--color-text-muted)] line-through'
              : 'text-[var(--color-text-primary)]'
          }`}
        >
          {item.text}
        </span>
        {status === 'in_progress' && (
          <div>
            <span
              className="text-[10px] font-semibold uppercase tracking-wide"
              style={{ color: brandColors.lightBlue }}
            >
              Doing
            </span>
          </div>
        )}
      </div>
      <span className="shrink-0 mt-0.5 p-0.5 text-[var(--color-text-muted)] opacity-60">
        <GripVertical size={16} />
      </span>
    </div>
  );
}

// ─── Add Action Item Input ───────────────────────────────────────────────────

function AddActionItemInput({
  onAdd,
  onCancel,
}: {
  onAdd: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handlePointerDownOutside = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (containerRef.current?.contains(target)) return;
      onCancel();
    };
    document.addEventListener('pointerdown', handlePointerDownOutside);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside);
    };
  }, [onCancel]);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (trimmed) {
      onAdd(trimmed);
      setText('');
      window.requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  };

  return (
    <div ref={containerRef} className="flex items-center gap-2 px-2 py-1.5">
      <div
        className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
        style={{ border: '2px solid var(--color-border)' }}
      />
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        placeholder="Add a task..."
        className="flex-1 text-sm bg-transparent text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none"
      />
      <button
        onClick={handleSubmit}
        disabled={!text.trim()}
        className="text-xs font-medium px-2 py-1 rounded-md transition-colors disabled:opacity-30"
        style={{ color: 'var(--color-brand-primary)' }}
      >
        Add
      </button>
    </div>
  );
}

function KanbanSwimlane({
  columnId,
  title,
  items,
  taskId,
  onToggle,
  onDelete,
  animateToDoneIds,
  animateToTodoIds,
  addComposer,
  dropTargetItemId,
  showColumnDropIndicator = false,
  orientation = 'vertical',
}: {
  columnId: ActionItemStatus;
  title: string;
  items: ActionItem[];
  taskId: string;
  onToggle: (taskId: string, actionItemId: string, completed: boolean) => Promise<void>;
  onDelete: (taskId: string, actionItemId: string) => Promise<void>;
  animateToDoneIds?: Record<string, true>;
  animateToTodoIds?: Record<string, true>;
  addComposer?: React.ReactNode;
  dropTargetItemId?: string | null;
  showColumnDropIndicator?: boolean;
  orientation?: KanbanOrientation;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column:${columnId}`,
  });

  return (
    <div
      ref={setNodeRef}
      className="rounded-lg p-2"
      style={{
        background: isOver ? 'var(--color-surface-elevated)' : 'var(--color-surface)',
        border: `1px solid ${isOver ? brandColors.teal : 'var(--color-border)'}`,
        ...(orientation === 'horizontal' ? { minWidth: 180, flex: 1 } : {}),
      }}
    >
      <div className="px-1 pb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
          {title}
        </p>
        <span className="text-[10px] text-[var(--color-text-muted)]">
          {items.length}
        </span>
      </div>

      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className="pb-1">
          <div className={`flex flex-col gap-2 ${items.length === 0 ? 'min-h-[18px]' : ''}`}>
            {showColumnDropIndicator && (
              <div className="mx-1 mt-1 mb-1 pointer-events-none">
                <div
                  className="h-[3px] rounded-full"
                  style={{
                    background: 'var(--color-success)',
                    boxShadow: '0 0 0 2px color-mix(in srgb, var(--color-success) 28%, transparent)',
                  }}
                />
              </div>
            )}
            {items.length === 0 && !addComposer && (
              <div
                className="h-[14px] rounded-md border border-dashed"
                style={{
                  borderColor: 'var(--color-border)',
                  background: 'transparent',
                }}
              />
            )}
            {items.map((item) => (
              <SortableActionItem
                key={item.id}
                item={item}
                taskId={taskId}
                onToggle={onToggle}
                onDelete={onDelete}
                layout="kanban"
                animateToDone={Boolean(animateToDoneIds?.[item.id])}
                animateToTodo={Boolean(animateToTodoIds?.[item.id])}
                showDropIndicatorTop={dropTargetItemId === item.id}
              />
            ))}
            {addComposer}
          </div>
        </div>
      </SortableContext>
    </div>
  );
}

// ─── Task Card (flat, always expanded) ───────────────────────────────────────

function buildStatusMap(items: ActionItem[]): Record<string, ActionItemStatus> {
  return Object.fromEntries(items.map((item) => [item.id, getActionItemStatus(item)]));
}

function normalizeOrderedItems(items: ActionItem[]): ActionItem[] {
  return items.map((item, index) => ({
    ...item,
    order: index,
    status: getActionItemStatus(item),
    completed: isActionItemDone(item),
  }));
}

function useTaskItemDnD({
  taskId,
  items,
  onReorder,
}: {
  taskId: string;
  items: ActionItem[];
  onReorder: (
    taskId: string,
    orderedItemIds: string[],
    statusByItemId?: Partial<Record<string, ActionItemStatus>>
  ) => Promise<void>;
}) {
  const [kanbanItems, setKanbanItems] = useState<ActionItem[]>(items);
  const itemsKey = items
    .map((item) => `${item.id}:${item.order}:${getActionItemStatus(item)}:${isActionItemDone(item) ? '1' : '0'}`)
    .join('|');

  useEffect(() => {
    setKanbanItems(items);
  }, [taskId, itemsKey, items]);

  const handleListDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = items.map((i) => i.id);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      const reordered = [...ids];
      reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, active.id as string);
      void onReorder(taskId, reordered);
    },
    [items, taskId, onReorder]
  );

  const moveKanbanItemToStatus = useCallback(
    (itemId: string, targetStatus: ActionItemStatus, insertAtStart = false) => {
      const current = [...kanbanItems];
      const activeIndex = current.findIndex((item) => item.id === itemId);
      if (activeIndex < 0) return;

      const activeItem = current[activeIndex];
      current.splice(activeIndex, 1);
      const { completedAt: _completedAt, ...rest } = activeItem;
      const movedItem: ActionItem = targetStatus === 'done'
        ? { ...rest, status: 'done', completed: true, completedAt: activeItem.completedAt }
        : { ...rest, status: targetStatus, completed: false };

      const firstTargetIndex = current.findIndex((item) => getActionItemStatus(item) === targetStatus);
      const lastTargetIndex = current.reduce((last, item, index) => (
        getActionItemStatus(item) === targetStatus ? index : last
      ), -1);
      const insertIndex = insertAtStart
        ? (firstTargetIndex >= 0 ? firstTargetIndex : current.length)
        : (lastTargetIndex >= 0 ? lastTargetIndex + 1 : current.length);

      current.splice(insertIndex, 0, movedItem);
      const normalized = normalizeOrderedItems(current);
      setKanbanItems(normalized);
      void onReorder(taskId, normalized.map((item) => item.id), buildStatusMap(normalized));
    },
    [kanbanItems, taskId, onReorder]
  );

  const handleKanbanDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) {
        setKanbanItems(items);
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);
      const current = [...kanbanItems];
      const activeIndex = current.findIndex((item) => item.id === activeId);
      if (activeIndex < 0) return;

      const activeItem = current[activeIndex];
      const overItem = current.find((item) => item.id === overId);
      const targetStatus: ActionItemStatus | null = overId.startsWith('column:')
        ? (overId.replace('column:', '') as ActionItemStatus)
        : (overItem ? getActionItemStatus(overItem) : null);
      if (!targetStatus) return;

      const next = [...current];
      next.splice(activeIndex, 1);

      const { completedAt: _completedAt, ...rest } = activeItem;
      const movedItem: ActionItem = targetStatus === 'done'
        ? { ...rest, status: 'done', completed: true, completedAt: activeItem.completedAt }
        : { ...rest, status: targetStatus, completed: false };

      let insertIndex = next.length;
      if (overItem) {
        const overIndex = next.findIndex((item) => item.id === overItem.id);
        if (overIndex >= 0) insertIndex = overIndex;
      } else {
        for (let i = next.length - 1; i >= 0; i--) {
          if (getActionItemStatus(next[i]) === targetStatus) {
            insertIndex = i + 1;
            break;
          }
        }
      }

      next.splice(insertIndex, 0, movedItem);
      const normalized = normalizeOrderedItems(next);

      setKanbanItems(normalized);
      void onReorder(taskId, normalized.map((item) => item.id), buildStatusMap(normalized));
    },
    [items, kanbanItems, taskId, onReorder]
  );

  return {
    kanbanItems,
    moveKanbanItemToStatus,
    handleListDragEnd,
    handleKanbanDragEnd,
  };
}

function TaskActionItems({
  task,
  onToggle,
  onAddItem,
  onDeleteItem,
  onReorder,
  adding,
  onCloseAdd,
  forceListLayout = false,
  externalDndContext = false,
  parentKind = 'task',
  visibleItemIds,
  waveDoneIds,
}: {
  task: TaskPlan;
  onToggle: (taskId: string, actionItemId: string, completed: boolean) => Promise<void>;
  onAddItem: (taskId: string, text: string) => Promise<unknown>;
  onDeleteItem: (taskId: string, actionItemId: string) => Promise<void>;
  onReorder: (
    taskId: string,
    orderedItemIds: string[],
    statusByItemId?: Partial<Record<string, ActionItemStatus>>
  ) => Promise<void>;
  adding: boolean;
  /** Items being wave-completed — show as done optimistically before Firestore confirms. */
  waveDoneIds?: Record<string, true>;
  onCloseAdd: () => void;
  /** When true, action items always render as a list (avoids nested Kanban DnD). */
  forceListLayout?: boolean;
  /**
   * When true, this card relies on an outer (page-level) `DndContext` for
   * action-item drags. In that mode we skip the internal DndContext +
   * DragOverlay and use globally-unique sortable ids so items can be
   * dragged across cards and onto outer drop targets (Schedule chips,
   * project Kanban columns, …).
   *
   * Outer page is responsible for handling `dragEnd` for these ids — see
   * `parseActionItemDragId` in `src/utils/todoDragIds.ts`.
   */
  externalDndContext?: boolean;
  /** Required when `externalDndContext` is true so the parser can route dragEnd back to the right document type. */
  parentKind?: ActionItemParentKind;
  /**
   * Optional projection filter — when provided, only action items whose
   * `id` is in the set are rendered. Used by the Projects pivot to show
   * the *same* TaskPlan document in every column where it has matching
   * items, with each column rendering just its slice of the items.
   * `undefined` means "render every item" (default).
   */
  visibleItemIds?: ReadonlySet<string>;
}) {
  const { taskViewMode: viewMode, kanbanOrientation } = useTaskView();
  // 'bullets' is a workspace-level layout, not a per-card layout — inside a
  // single card we treat it as 'list' so the existing action-item renderer
  // keeps working (the outline projection is built outside this component).
  const effectiveCardViewMode = viewMode === 'bullets' ? 'list' : viewMode;
  const resolvedMode = forceListLayout ? 'list' : effectiveCardViewMode;
  const [activeDragItemId, setActiveDragItemId] = useState<string | null>(null);
  const [listDropTargetId, setListDropTargetId] = useState<string | null>(null);
  const [kanbanDropTargetId, setKanbanDropTargetId] = useState<string | null>(null);
  const [kanbanDropColumnId, setKanbanDropColumnId] = useState<ActionItemStatus | null>(null);
  const [recentlyDoneItemIds, setRecentlyDoneItemIds] = useState<Record<string, true>>({});
  const [recentlyTodoItemIds, setRecentlyTodoItemIds] = useState<Record<string, true>>({});
  const doneAnimationTimersRef = useRef<Record<string, number>>({});
  const todoAnimationTimersRef = useRef<Record<string, number>>({});
  const viewContentRef = useRef<HTMLDivElement | null>(null);
  const items = useMemo(
    () => {
      const all = [...task.actionItems]
        .map((item) => ({ ...item, status: getActionItemStatus(item), completed: isActionItemDone(item) }))
        .sort((a, b) => a.order - b.order);
      // Projection filter: when the page (e.g. Projects pivot's column
      // rendering) wants only a subset of this card's items, drop the rest.
      // The unfiltered `task.actionItems` is still passed through to all
      // mutation handlers (toggle / reorder / delete) so writes target the
      // canonical document — we only narrow what's *rendered*.
      if (visibleItemIds) {
        return all.filter((it) => visibleItemIds.has(it.id));
      }
      return all;
    },
    [task.actionItems, visibleItemIds]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const kanbanCollisionDetection = useCallback<CollisionDetection>((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  }, []);

  const { kanbanItems, moveKanbanItemToStatus, handleListDragEnd, handleKanbanDragEnd } = useTaskItemDnD({
    taskId: task.id,
    items,
    onReorder,
  });
  const activeDragItem = useMemo(
    () => (kanbanItems.find((item) => item.id === activeDragItemId)
      ?? items.find((item) => item.id === activeDragItemId)
      ?? null),
    [activeDragItemId, kanbanItems, items]
  );
  const clearDropTargets = useCallback(() => {
    setListDropTargetId(null);
    setKanbanDropTargetId(null);
    setKanbanDropColumnId(null);
  }, []);
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragItemId(String(event.active.id));
    clearDropTargets();
  }, [clearDropTargets]);
  const handleDragCancel = useCallback(() => {
    setActiveDragItemId(null);
    clearDropTargets();
  }, [clearDropTargets]);
  const handleListDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over?.id ? String(event.over.id) : null;
    if (!overId || overId === activeDragItemId || overId.startsWith('column:')) {
      setListDropTargetId(null);
      return;
    }
    setListDropTargetId(overId);
  }, [activeDragItemId]);
  const handleKanbanDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over?.id ? String(event.over.id) : null;
    if (!overId || overId === activeDragItemId) {
      setKanbanDropTargetId(null);
      setKanbanDropColumnId(null);
      return;
    }

    if (overId.startsWith('column:')) {
      setKanbanDropTargetId(null);
      setKanbanDropColumnId(overId.replace('column:', '') as ActionItemStatus);
      return;
    }

    const overItem = kanbanItems.find((item) => item.id === overId);
    if (!overItem) {
      setKanbanDropTargetId(null);
      setKanbanDropColumnId(null);
      return;
    }

    setKanbanDropTargetId(overId);
    setKanbanDropColumnId(getActionItemStatus(overItem));
  }, [activeDragItemId, kanbanItems]);
  const handleListDragEndWithOverlay = useCallback((event: DragEndEvent) => {
    setActiveDragItemId(null);
    clearDropTargets();
    handleListDragEnd(event);
  }, [clearDropTargets, handleListDragEnd]);
  const handleKanbanDragEndWithOverlay = useCallback((event: DragEndEvent) => {
    setActiveDragItemId(null);
    clearDropTargets();
    handleKanbanDragEnd(event);
  }, [clearDropTargets, handleKanbanDragEnd]);

  useEffect(() => () => {
    Object.values(doneAnimationTimersRef.current).forEach((timer) => {
      window.clearTimeout(timer);
    });
    Object.values(todoAnimationTimersRef.current).forEach((timer) => {
      window.clearTimeout(timer);
    });
    doneAnimationTimersRef.current = {};
    todoAnimationTimersRef.current = {};
  }, []);

  const handleKanbanToggle = useCallback(
    async (_taskId: string, actionItemId: string, completed: boolean) => {
      if (completed) {
        setRecentlyDoneItemIds((prev) => ({ ...prev, [actionItemId]: true }));
        setRecentlyTodoItemIds((prev) => {
          const next = { ...prev };
          delete next[actionItemId];
          return next;
        });
        if (doneAnimationTimersRef.current[actionItemId]) {
          window.clearTimeout(doneAnimationTimersRef.current[actionItemId]);
        }
        if (todoAnimationTimersRef.current[actionItemId]) {
          window.clearTimeout(todoAnimationTimersRef.current[actionItemId]);
          delete todoAnimationTimersRef.current[actionItemId];
        }
        doneAnimationTimersRef.current[actionItemId] = window.setTimeout(() => {
          setRecentlyDoneItemIds((prev) => {
            const next = { ...prev };
            delete next[actionItemId];
            return next;
          });
          delete doneAnimationTimersRef.current[actionItemId];
        }, 1400);
      } else {
        setRecentlyDoneItemIds((prev) => {
          const next = { ...prev };
          delete next[actionItemId];
          return next;
        });
        setRecentlyTodoItemIds((prev) => ({ ...prev, [actionItemId]: true }));
        if (doneAnimationTimersRef.current[actionItemId]) {
          window.clearTimeout(doneAnimationTimersRef.current[actionItemId]);
          delete doneAnimationTimersRef.current[actionItemId];
        }
        if (todoAnimationTimersRef.current[actionItemId]) {
          window.clearTimeout(todoAnimationTimersRef.current[actionItemId]);
        }
        todoAnimationTimersRef.current[actionItemId] = window.setTimeout(() => {
          setRecentlyTodoItemIds((prev) => {
            const next = { ...prev };
            delete next[actionItemId];
            return next;
          });
          delete todoAnimationTimersRef.current[actionItemId];
        }, 1400);
      }

      moveKanbanItemToStatus(actionItemId, completed ? 'done' : 'todo');
    },
    [moveKanbanItemToStatus]
  );

  const handleAdd = useCallback(
    (text: string) => {
      onAddItem(task.id, text);
    },
    [task.id, onAddItem]
  );

  useEffect(() => {
    const container = viewContentRef.current;
    if (!container) return;

    container.animate(
      [
        { opacity: 0.78, transform: 'translateY(4px) scale(0.995)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      { duration: 840, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );

    const nodes = Array.from(container.querySelectorAll<HTMLElement>('[data-task-view-item="true"]'));
    nodes.forEach((node, index) => {
      node.animate(
        [
          {
            opacity: 0,
            transform: resolvedMode === 'kanban' ? 'translateY(8px) scale(0.99)' : 'translateX(-8px) scale(0.99)',
          },
          { opacity: 1, transform: 'translate(0, 0) scale(1)' },
        ],
        {
          duration: 960,
          delay: Math.min(index * 140, 840),
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both',
        }
      );
    });
  }, [resolvedMode, task.id]);

  // When the parent page owns the DndContext, build globally-unique sortable
  // ids for our action items so cross-card moves and drops onto outer
  // targets (Schedule chips, project columns) can be parsed back to the
  // owning document. The list of ids is what `SortableContext` uses to
  // animate gap creation, so it must match the ids each `useSortable` reports.
  const sortableIdForItem = useCallback(
    (itemId: string): string =>
      externalDndContext
        ? makeActionItemDragId(parentKind, task.id, itemId)
        : itemId,
    [externalDndContext, parentKind, task.id]
  );

  const sortableIds = useMemo(
    () => items.map((i) => sortableIdForItem(i.id)),
    [items, sortableIdForItem]
  );

  return (
    <div className="px-2 py-2 flex flex-col">
      <div ref={viewContentRef}>
        {resolvedMode === 'list' ? (
          externalDndContext ? (
            // Page-level DndContext owns drag-end. We just render the
            // sortable items so dnd-kit can animate the gap and so the
            // page-level dispatch can identify them via prefixed ids.
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              {items.map((item) => (
                <SortableActionItem
                  key={item.id}
                  item={item}
                  taskId={task.id}
                  onToggle={onToggle}
                  onDelete={onDeleteItem}
                  sortableId={sortableIdForItem(item.id)}
                  animateToDone={Boolean(waveDoneIds?.[item.id]) && !isActionItemDone(item)}
                  waveDone={Boolean(waveDoneIds?.[item.id])}
                />
              ))}
            </SortableContext>
          ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleListDragOver}
          onDragCancel={handleDragCancel}
          onDragEnd={handleListDragEndWithOverlay}
        >
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {items.map((item) => (
              <SortableActionItem
                key={item.id}
                item={item}
                taskId={task.id}
                onToggle={onToggle}
                onDelete={onDeleteItem}
                showDropIndicatorTop={listDropTargetId === item.id}
                animateToDone={Boolean(waveDoneIds?.[item.id]) && !isActionItemDone(item)}
                waveDone={Boolean(waveDoneIds?.[item.id])}
              />
            ))}
          </SortableContext>
          {typeof document !== 'undefined'
            ? createPortal(
              <DragOverlay zIndex={9999}>
                {activeDragItem ? <DragActionItemOverlay item={activeDragItem} /> : null}
              </DragOverlay>,
              document.body
            )
            : null}
        </DndContext>
          )
        ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={kanbanCollisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleKanbanDragOver}
          onDragCancel={handleDragCancel}
          onDragEnd={handleKanbanDragEndWithOverlay}
        >
          <div className={`flex gap-2 ${kanbanOrientation === 'horizontal' ? 'flex-row overflow-x-auto pb-1' : 'flex-col'}`}>
            {KANBAN_COLUMNS.map((column) => {
              const columnItems = kanbanItems.filter((item) => getActionItemStatus(item) === column.id);
              const showColumnDropIndicator = Boolean(activeDragItemId)
                && kanbanDropColumnId === column.id
                && !kanbanDropTargetId;

              return (
                <KanbanSwimlane
                  key={column.id}
                  columnId={column.id}
                  title={column.label}
                  items={columnItems}
                  taskId={task.id}
                  onToggle={handleKanbanToggle}
                  onDelete={onDeleteItem}
                  animateToDoneIds={recentlyDoneItemIds}
                  animateToTodoIds={recentlyTodoItemIds}
                  addComposer={adding && column.id === 'todo' ? (
                    <AddActionItemInput
                      onAdd={handleAdd}
                      onCancel={onCloseAdd}
                    />
                  ) : null}
                  dropTargetItemId={kanbanDropColumnId === column.id ? kanbanDropTargetId : null}
                  showColumnDropIndicator={showColumnDropIndicator}
                  orientation={kanbanOrientation}
                />
              );
            })}
          </div>
          {typeof document !== 'undefined'
            ? createPortal(
              <DragOverlay zIndex={9999}>
                {activeDragItem ? <DragActionItemOverlay item={activeDragItem} /> : null}
              </DragOverlay>,
              document.body
            )
            : null}
        </DndContext>
        )}
      </div>

      {adding && resolvedMode === 'list' && (
        <AddActionItemInput
          onAdd={handleAdd}
          onCancel={onCloseAdd}
        />
      )}
    </div>
  );
}

export function TaskCard({
  task,
  onToggle,
  onAddItem,
  onDeleteItem,
  onReorder,
  variant = 'task',
  badgeLabel,
  dateBadge,
  collapsed,
  onToggleCollapse,
  forceActionListLayout = false,
  suppressViewToggleButtons = false,
  externalDndContext = false,
  visibleItemIds,
  projects,
  currentProject,
  onAssignProject,
  onCreateProject,
}: {
  task: TaskPlan;
  onToggle: (taskId: string, actionItemId: string, completed: boolean) => Promise<void>;
  onAddItem: (taskId: string, text: string) => Promise<unknown>;
  onDeleteItem: (taskId: string, actionItemId: string) => Promise<void>;
  onReorder: (
    taskId: string,
    orderedItemIds: string[],
    statusByItemId?: Partial<Record<string, ActionItemStatus>>
  ) => Promise<void>;
  variant?: 'task' | 'dayFocus';
  badgeLabel?: string;
  dateBadge?: { day: number; month: string };
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Force action items to list layout (e.g. Todos workspace board). */
  forceActionListLayout?: boolean;
  /** Hide list/Kanban/orientation controls on the card header. */
  suppressViewToggleButtons?: boolean;
  /**
   * When true, action item drags register with the nearest outer
   * `DndContext` instead of the card's own. Set this on TodosPage so that
   * sub-items can be dragged across cards / onto Schedule chips / onto
   * project Kanban columns. Other surfaces (Home, Today, Timeline) leave
   * this off and keep the per-card drag context for in-card reorder only.
   */
  externalDndContext?: boolean;
  /**
   * Optional set of action-item ids to render. Used by the Projects pivot
   * (and Schedule kanban) to project a single TaskPlan into multiple
   * columns — each column renders the same card document but narrowed to
   * the items whose status matches that column. `undefined` = render all.
   * The data model is unchanged; this is purely a render-time filter.
   */
  visibleItemIds?: ReadonlySet<string>;
  /** Projects available for assignment via the card ellipsis menu. */
  projects?: TodoProject[];
  /** Currently assigned project, or null if the card is in Inbox. */
  currentProject?: TodoProject | null;
  /** Called when the user picks a project (or null to clear). */
  onAssignProject?: (projectId: string | null) => Promise<void> | void;
  /** Called when the user creates a new project inline. */
  onCreateProject?: (name: string, colorKey?: TodoProjectColorKey) => Promise<string | null> | string | null;
}) {
  const { taskViewMode: resolvedViewMode, setTaskViewMode, kanbanOrientation, setKanbanOrientation } = useTaskView();
  const isComplete = task.status === 'completed';
  const isDayFocus = variant === 'dayFocus';
  const [copied, setCopied] = useState(false);
  const [adding, setAdding] = useState(false);
  // Description is collapsed by default per user feedback — full summary
  // text is too noisy when scanning a board of cards. Click the chevron
  // (or the "i" hint) on the header to reveal it.
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const hasSummary = Boolean(task.summary && task.summary.trim().length > 0);
  const [waveCompletingIds, setWaveCompletingIds] = useState<Record<string, true>>({});
  const [waving, setWaving] = useState(false);
  const waveTimersRef = useRef<number[]>([]);
  const [cardMenuOpen, setCardMenuOpen] = useState(false);
  const [cardMenuPos, setCardMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [projectPickerPos, setProjectPickerPos] = useState<{ top: number; left: number } | null>(null);
  const cardMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const cardMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (collapsed) setAdding(false);
  }, [collapsed]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(taskToMarkdown(task));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCompleteAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (waving) return;
      const undone = task.actionItems
        .filter((i) => !isActionItemDone(i))
        .sort((a, b) => a.order - b.order);
      if (undone.length === 0) return;

      setWaving(true);
      const STAGGER_MS = 130;

      undone.forEach((item, index) => {
        const t = window.setTimeout(() => {
          setWaveCompletingIds((prev) => ({ ...prev, [item.id]: true }));
          void onToggle(task.id, item.id, true);
        }, index * STAGGER_MS);
        waveTimersRef.current.push(t);
      });

      const cleanupTimer = window.setTimeout(() => {
        setWaving(false);
        waveTimersRef.current = [];
      }, undone.length * STAGGER_MS + 400);
      waveTimersRef.current.push(cleanupTimer);
    },
    [waving, task.actionItems, task.id, onToggle]
  );

  useEffect(
    () => () => {
      waveTimersRef.current.forEach((t) => window.clearTimeout(t));
    },
    []
  );

  const handleCardMenuOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    const trigger = cardMenuTriggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setCardMenuPos({ top: rect.bottom + 4, left: rect.left });
    setCardMenuOpen(true);
  };

  useEffect(() => {
    if (!cardMenuOpen) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (cardMenuRef.current?.contains(target)) return;
      if (cardMenuTriggerRef.current?.contains(target)) return;
      setCardMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCardMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [cardMenuOpen]);

  const handleOpenProjectPicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCardMenuOpen(false);
    const trigger = cardMenuTriggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const popoverWidth = 280;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - popoverWidth - 8);
    setProjectPickerPos({ top: rect.bottom + 4, left });
    setProjectPickerOpen(true);
  };

  return (
    <div
      className="flank-card rounded-xl overflow-hidden"
      style={
        isDayFocus
          ? {
              background: `linear-gradient(135deg, ${brandColors.teal}10, ${brandColors.teal}1A)`,
              border: `1px solid ${brandColors.teal}55`,
            }
          : undefined
      }
    >
      <div
        className={`flex items-stretch${onToggleCollapse ? ' cursor-pointer select-none' : ''}`}
        style={{ borderBottom: '1px solid var(--color-border)' }}
        onClick={onToggleCollapse}
      >
        {dateBadge && (
          <div
            className="shrink-0 flex flex-col items-center justify-center px-3 py-2 gap-0.5"
            style={{ borderRight: '1px solid var(--color-border)', minWidth: 44 }}
          >
            <span className="text-base font-bold leading-none text-[var(--color-text-primary)]">{dateBadge.day}</span>
            <span className="text-[9px] uppercase leading-none text-[var(--color-text-muted)] font-medium tracking-wide">{dateBadge.month}</span>
          </div>
        )}
        <div className="flex-1 px-4 py-3 flex items-center gap-3 min-w-0">
          {isDayFocus ? (
            <Eye size={16} style={{ color: isComplete ? 'var(--color-success)' : brandColors.teal, flexShrink: 0 }} />
          ) : (
            <Target size={16} style={{ color: isComplete ? 'var(--color-success)' : brandColors.darkBlue, flexShrink: 0 }} />
          )}
          <span className={`flex-1 text-sm font-semibold truncate ${isComplete ? 'text-[var(--color-text-muted)] line-through' : 'text-[var(--color-text-primary)]'}`}>
            {task.title}
          </span>
          {badgeLabel && (
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0"
              style={{ background: `${brandColors.teal}22`, color: brandColors.teal }}
            >
              {badgeLabel}
            </span>
          )}
          {hasSummary && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setDescriptionOpen((v) => !v);
              }}
              className="p-1.5 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
              title={descriptionOpen ? 'Hide description' : 'Show description'}
              aria-label={descriptionOpen ? 'Hide description' : 'Show description'}
              aria-expanded={descriptionOpen}
            >
              <Info
                size={14}
                style={{
                  color: descriptionOpen
                    ? brandColors.lightBlue
                    : 'var(--color-text-muted)',
                }}
              />
            </button>
          )}
          {!suppressViewToggleButtons && (
          <>
          <button
            onClick={(e) => { e.stopPropagation(); setTaskViewMode(resolvedViewMode === 'list' ? 'kanban' : 'list'); }}
            className="p-1.5 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
            title={resolvedViewMode === 'list' ? 'Switch to kanban view' : 'Switch to list view'}
          >
            {resolvedViewMode === 'list'
              ? <Columns3 size={14} style={{ color: 'var(--color-text-muted)' }} />
              : <List size={14} style={{ color: 'var(--color-text-muted)' }} />
            }
          </button>
          {resolvedViewMode === 'kanban' && (
            <button
              onClick={(e) => { e.stopPropagation(); setKanbanOrientation(kanbanOrientation === 'horizontal' ? 'vertical' : 'horizontal'); }}
              className="p-1.5 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
              title={kanbanOrientation === 'horizontal' ? 'Switch to vertical kanban' : 'Switch to horizontal kanban'}
            >
              {kanbanOrientation === 'horizontal'
                ? <Rows3 size={14} style={{ color: 'var(--color-text-muted)' }} />
                : <Columns3 size={14} style={{ color: 'var(--color-text-muted)' }} />
              }
            </button>
          )}
          </>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setAdding(true); }}
            className="p-1.5 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
            title="Add task"
          >
            <Plus size={14} style={{ color: 'var(--color-text-muted)' }} />
          </button>
          <button
            onClick={handleCopy}
            className="p-1.5 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
            title="Copy as Markdown"
          >
            {copied ? (
              <CheckCircle size={14} style={{ color: 'var(--color-success)' }} />
            ) : (
              <Copy size={14} style={{ color: 'var(--color-text-muted)' }} />
            )}
          </button>
          {task.actionItems.some((i) => !isActionItemDone(i)) && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={handleCompleteAll}
              disabled={waving}
              className={`p-1.5 rounded-md transition-colors shrink-0 ${waving ? 'flank-complete-all-waving' : 'hover:bg-[var(--color-surface)]'}`}
              title="Complete all tasks"
              aria-label="Complete all tasks"
            >
              <CheckCheck
                size={14}
                style={{ color: waving ? 'var(--color-success)' : 'var(--color-text-muted)' }}
              />
            </button>
          )}
          <button
            ref={cardMenuTriggerRef}
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handleCardMenuOpen}
            className="p-1.5 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
            title="More options"
            aria-label="More options"
            aria-haspopup="menu"
            aria-expanded={cardMenuOpen}
          >
            <MoreHorizontal size={14} style={{ color: 'var(--color-text-muted)' }} />
          </button>
          {onToggleCollapse && (
            <span className="p-1.5 shrink-0" aria-hidden>
              {collapsed
                ? <ChevronDown size={14} style={{ color: 'var(--color-text-muted)' }} />
                : <ChevronUp size={14} style={{ color: 'var(--color-text-muted)' }} />
              }
            </span>
          )}
        </div>
      </div>

      {!collapsed && hasSummary && descriptionOpen && (
        <p className="px-4 pt-2 pb-1 text-xs text-[var(--color-text-secondary)] leading-relaxed">
          {task.summary}
        </p>
      )}

      {!collapsed && (
        <TaskActionItems
          task={task}
          onToggle={onToggle}
          onAddItem={onAddItem}
          onDeleteItem={onDeleteItem}
          onReorder={onReorder}
          adding={adding}
          onCloseAdd={() => setAdding(false)}
          forceListLayout={forceActionListLayout}
          externalDndContext={externalDndContext}
          parentKind={variant === 'dayFocus' ? 'focus' : 'task'}
          visibleItemIds={visibleItemIds}
          waveDoneIds={waveCompletingIds}
        />
      )}

      {cardMenuOpen && cardMenuPos
        ? createPortal(
            <div
              ref={cardMenuRef}
              role="menu"
              className="fixed z-50 py-1 rounded-xl shadow-xl"
              style={{
                top: cardMenuPos.top,
                left: cardMenuPos.left,
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                minWidth: 200,
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
              }}
            >
              {onAssignProject && projects !== undefined && (
                <button
                  type="button"
                  role="menuitem"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={handleOpenProjectPicker}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors hover:bg-[var(--color-surface)]"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <Folder size={14} className="shrink-0 text-[var(--color-text-muted)]" />
                  <span className="flex-1">Assign to project</span>
                  {currentProject && (
                    <span className="text-xs text-[var(--color-text-muted)] truncate max-w-[80px]">
                      {currentProject.name}
                    </span>
                  )}
                </button>
              )}
            </div>,
            document.body,
          )
        : null}

      {projectPickerOpen && projectPickerPos && onAssignProject && onCreateProject && projects !== undefined
        ? createPortal(
            <ProjectPickerPopover
              anchor={projectPickerPos}
              projects={projects}
              current={currentProject ?? null}
              onClose={() => setProjectPickerOpen(false)}
              onAssign={async (projectId) => {
                await onAssignProject(projectId);
                setProjectPickerOpen(false);
              }}
              onCreate={async (name, colorKey) => {
                const id = await onCreateProject(name, colorKey);
                if (id) {
                  await onAssignProject(id);
                }
                setProjectPickerOpen(false);
              }}
            />,
            document.body,
          )
        : null}
    </div>
  );
}

// ─── Collapsible Task Card (for HomePage) ────────────────────────────────────

export function CollapsibleTaskCard({
  task,
  open,
  onOpenToggle,
  onToggle,
  onAddItem,
  onDeleteItem,
  onReorder,
  animationDelay = 0,
  isCompleting = false,
  variant = 'task',
  badgeLabel,
}: {
  task: TaskPlan;
  open: boolean;
  onOpenToggle: () => void;
  onToggle: (taskId: string, actionItemId: string, completed: boolean) => Promise<void>;
  onAddItem: (taskId: string, text: string) => Promise<unknown>;
  onDeleteItem: (taskId: string, actionItemId: string) => Promise<void>;
  onReorder: (
    taskId: string,
    orderedItemIds: string[],
    statusByItemId?: Partial<Record<string, ActionItemStatus>>
  ) => Promise<void>;
  animationDelay?: number;
  isCompleting?: boolean;
  variant?: 'task' | 'dayFocus';
  badgeLabel?: string;
}) {
  const { taskViewMode: resolvedViewMode, setTaskViewMode, kanbanOrientation, setKanbanOrientation } = useTaskView();
  const [copied, setCopied] = useState(false);
  const [adding, setAdding] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const titleRef = useRef<HTMLSpanElement>(null);
  const done = task.actionItems.filter((i) => isActionItemDone(i)).length;
  const total = task.actionItems.length;
  const isComplete = task.status === 'completed' || (total > 0 && done === total);
  const isDayFocus = variant === 'dayFocus';
  const shouldAnimateIn = animationDelay > 0;

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [task.title]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(taskToMarkdown(task));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`flank-card rounded-xl overflow-hidden ${shouldAnimateIn ? 'flank-appear' : ''}`}
      style={{
        animationDelay: shouldAnimateIn ? `${animationDelay}ms` : undefined,
        ...(isDayFocus
          ? {
              background: `linear-gradient(135deg, ${brandColors.teal}10, ${brandColors.teal}1A)`,
              border: `1px solid ${brandColors.teal}55`,
            }
          : {}),
      }}
    >
      <button
        onClick={() => {
          if (isCompleting) return;
          onOpenToggle();
        }}
        className="w-full px-4 py-3 flex items-center gap-3 text-left"
        style={{ borderBottom: open ? '1px solid var(--color-border)' : undefined }}
        title={!open && isTruncated ? task.title : undefined}
      >
        {isDayFocus ? (
          <Eye
            size={16}
            style={{
              color: isComplete ? 'var(--color-success)' : brandColors.teal,
              flexShrink: 0,
            }}
          />
        ) : (
          <Target
            size={16}
            style={{
              color: isComplete ? 'var(--color-success)' : brandColors.darkBlue,
              flexShrink: 0,
            }}
          />
        )}
        <span
          ref={titleRef}
          className={`flex-1 text-sm font-semibold truncate ${
            isComplete
              ? 'text-[var(--color-text-muted)] line-through'
              : 'text-[var(--color-text-primary)]'
          }`}
        >
          {task.title}
        </span>
        {badgeLabel && (
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0"
            style={{ background: `${brandColors.teal}22`, color: brandColors.teal }}
          >
            {badgeLabel}
          </span>
        )}
        <span
          className="p-1 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
          role="button"
          title={resolvedViewMode === 'list' ? 'Switch to kanban view' : 'Switch to list view'}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            if (isCompleting) return;
            e.stopPropagation();
            setTaskViewMode(resolvedViewMode === 'list' ? 'kanban' : 'list');
          }}
        >
          {resolvedViewMode === 'list'
            ? <Columns3 size={14} style={{ color: 'var(--color-text-muted)' }} />
            : <List size={14} style={{ color: 'var(--color-text-muted)' }} />
          }
        </span>
        {resolvedViewMode === 'kanban' && (
          <span
            className="p-1 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
            role="button"
            title={kanbanOrientation === 'horizontal' ? 'Switch to vertical kanban' : 'Switch to horizontal kanban'}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              if (isCompleting) return;
              e.stopPropagation();
              setKanbanOrientation(kanbanOrientation === 'horizontal' ? 'vertical' : 'horizontal');
            }}
          >
            {kanbanOrientation === 'horizontal'
              ? <Rows3 size={14} style={{ color: 'var(--color-text-muted)' }} />
              : <Columns3 size={14} style={{ color: 'var(--color-text-muted)' }} />
            }
          </span>
        )}
        <span
          className="p-1 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
          role="button"
          onClick={(e) => {
            if (isCompleting) return;
            e.stopPropagation();
            setAdding(true);
            if (!open) onOpenToggle();
          }}
        >
          <Plus size={14} style={{ color: 'var(--color-text-muted)' }} />
        </span>
        <span
          className="p-1 rounded-md hover:bg-[var(--color-surface)] transition-colors shrink-0"
          role="button"
          onClick={(e) => {
            if (isCompleting) return;
            handleCopy(e);
          }}
        >
          {copied ? (
            <CheckCircle size={14} style={{ color: 'var(--color-success)' }} />
          ) : (
            <Copy size={14} style={{ color: 'var(--color-text-muted)' }} />
          )}
        </span>
        <span
          className={`text-xs shrink-0 ${
            isComplete ? 'text-[var(--color-success)]' : 'text-[var(--color-text-muted)]'
          }`}
        >
          {done}/{total}
        </span>
        <ChevronDown
          size={16}
          className="shrink-0 transition-transform duration-200"
          style={{
            color: 'var(--color-text-muted)',
            transform: open ? 'rotate(180deg)' : undefined,
          }}
        />
      </button>

      {isTruncated && (
        <div
          className="px-4 py-2"
          style={!open ? { borderTop: '1px solid var(--color-border)' } : undefined}
        >
          <p
            className={`text-xs leading-relaxed ${
              isComplete
                ? 'text-[var(--color-text-muted)] line-through'
                : 'text-[var(--color-text-secondary)]'
            }`}
          >
            {task.title}
          </p>
        </div>
      )}

      <div
        className="grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          {task.summary && (
            <p className="px-4 pt-2 pb-1 text-xs text-[var(--color-text-secondary)] leading-relaxed">
              {task.summary}
            </p>
          )}

          <div style={{ pointerEvents: isCompleting ? 'none' : 'auto' }}>
            <TaskActionItems
              task={task}
              onToggle={onToggle}
              onAddItem={onAddItem}
              onDeleteItem={onDeleteItem}
              onReorder={onReorder}
              adding={adding}
              onCloseAdd={() => setAdding(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
